import React from "react";
import {
    CheckCircle,
    Clock,
    AlertTriangle,
    FileText,
    X,
    Pause,
    HelpCircle,
    AlertCircle,
    HandCoins,
    ArrowUpFromDot,
    CircleEllipsis,
    MessageSquareQuote,
    Mailbox,
    EarOff,
    CalendarSync,
    CalendarPlus,
    CalendarCheck,
    Gavel,
    PackageCheck,
    PencilRuler,
    Stamp,
    Truck,
    SquareX,
    ReceiptText,
    ShieldCheck,
    Loader,
    Hourglass,
    BookText,
    FileCheck,
    FilePen,
    Timer,
    AlertOctagon,
    ClipboardCheck,
    Award,
    ClipboardEdit,
    BookOpen,
    ShieldAlert,
    FileWarning,
    Eye,
    Send,
    Scale
} from 'lucide-react';

export enum Status {
    UCDPREADYTOUPLOAD = 1,
    PENDINGPAYMENT = 2,
    DEFAULT = 3,
    ASSIGNED = 4,
    ACCEPTED = 5,
    INPROGRESS = 6,
    REFERTOCOMMENTS = 7,
    LEFTMESSAGE = 8,
    NOANSWER = 9,
    ONHOLD = 10,
    SCHEDULED = 11,
    RESCHEDULED = 12,
    INSPECTED = 13,
    INREVIEW = 14,
    COMPLETED = 15,
    REVISIONREQUESTED = 16,
    REVIEWED = 17,
    DELIVEREDTOCLIENT = 18,
    CANCELLED = 19,
    UCDPFAIL = 20,
    REJECTED = 21,
    INVOICECREATED = 22,
    LENDERREVISIONREQUES = 23,
    REVISIONREQUESTEDRES = 24,
    LEVEL2QC = 25,
    WAITINGAPPROVAL = 26,
    DELAYED = 27,
    DRAFT = 28,
    QUOTE = 29,
    PENDINGLVL2 = 30,
    CANCELPENDING = 31,
    REVIEWESCALATION = 32,
    POSSIBLEREVISION = 33,
    SPOTHIRE = 34,
    APPRAISERDELAY = 35,
    LEVEL3QC = 36,
    DOCUMENTREVIEW = 37,
    PENDINGDELIVERY = 38,
    DUPLICATEREVIEW = 39,
    CHASEESCALATION = 40,
    LEADERSHIPQC = 41,
}

// Define the color types we support (matching Tailwind's color palette)
export type ColorName = 'blue' | 'yellow' | 'gray' | 'green' | 'purple' | 'red' | 'orange';

export interface StatusMetadata {
    icon: React.ReactNode;
    color: ColorName;
    bgColor: string;
    textColor: string;
    iconColor: string;
}

// Define all status metadata directly with hardcoded color classes
export const statusMetadata: Record<Status, StatusMetadata> = {
    [Status.UCDPREADYTOUPLOAD]: {
        icon: <FileText />,
        color: 'blue',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-700',
        iconColor: 'text-blue-500'
    },
    [Status.PENDINGPAYMENT]: {
        icon: <HandCoins />,
        color: 'yellow',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        iconColor: 'text-yellow-500'
    },
    [Status.DEFAULT]: {
        icon: <HelpCircle />,
        color: 'gray',
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-700',
        iconColor: 'text-gray-500'
    },
    [Status.ASSIGNED]: {
        icon: <ArrowUpFromDot />,
        color: 'blue',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-700',
        iconColor: 'text-blue-500'
    },
    [Status.ACCEPTED]: {
        icon: <CheckCircle />,
        color: 'green',
        bgColor: 'bg-green-100',
        textColor: 'text-green-700',
        iconColor: 'text-green-500'
    },
    [Status.INPROGRESS]: {
        icon: <CircleEllipsis />,
        color: 'blue',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-700',
        iconColor: 'text-blue-500'
    },
    [Status.REFERTOCOMMENTS]: {
        icon: <MessageSquareQuote />,
        color: 'yellow',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        iconColor: 'text-yellow-500'
    },
    [Status.LEFTMESSAGE]: {
        icon: <Mailbox />,
        color: 'purple',
        bgColor: 'bg-purple-100',
        textColor: 'text-purple-700',
        iconColor: 'text-purple-500'
    },
    [Status.NOANSWER]: {
        icon: <EarOff />,
        color: 'red',
        bgColor: 'bg-red-100',
        textColor: 'text-red-700',
        iconColor: 'text-red-500'
    },
    [Status.ONHOLD]: {
        icon: <Pause />,
        color: 'yellow',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        iconColor: 'text-yellow-500'
    },
    [Status.SCHEDULED]: {
        icon: <CalendarPlus />,
        color: 'blue',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-700',
        iconColor: 'text-blue-500'
    },
    [Status.RESCHEDULED]: {
        icon: <CalendarSync />,
        color: 'yellow',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        iconColor: 'text-yellow-500'
    },
    [Status.INSPECTED]: {
        icon: <CalendarCheck />,
        color: 'green',
        bgColor: 'bg-green-100',
        textColor: 'text-green-700',
        iconColor: 'text-green-500'
    },
    [Status.INREVIEW]: {
        icon: <Gavel />,
        color: 'blue',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-700',
        iconColor: 'text-blue-500'
    },
    [Status.COMPLETED]: {
        icon: <PackageCheck />,
        color: 'green',
        bgColor: 'bg-green-100',
        textColor: 'text-green-700',
        iconColor: 'text-green-500'
    },
    [Status.REVISIONREQUESTED]: {
        icon: <PencilRuler />,
        color: 'red',
        bgColor: 'bg-red-100',
        textColor: 'text-red-700',
        iconColor: 'text-red-500'
    },
    [Status.REVIEWED]: {
        icon: <Stamp />,
        color: 'green',
        bgColor: 'bg-green-100',
        textColor: 'text-green-700',
        iconColor: 'text-green-500'
    },
    [Status.DELIVEREDTOCLIENT]: {
        icon: <Truck />,
        color: 'green',
        bgColor: 'bg-green-100',
        textColor: 'text-green-700',
        iconColor: 'text-green-500'
    },
    [Status.CANCELLED]: {
        icon: <X />,
        color: 'red',
        bgColor: 'bg-red-100',
        textColor: 'text-red-700',
        iconColor: 'text-red-500'
    },
    [Status.UCDPFAIL]: {
        icon: <AlertTriangle />,
        color: 'red',
        bgColor: 'bg-red-100',
        textColor: 'text-red-700',
        iconColor: 'text-red-500'
    },
    [Status.REJECTED]: {
        icon: <SquareX />,
        color: 'red',
        bgColor: 'bg-red-100',
        textColor: 'text-red-700',
        iconColor: 'text-red-500'
    },
    [Status.INVOICECREATED]: {
        icon: <ReceiptText />,
        color: 'blue',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-700',
        iconColor: 'text-blue-500'
    },
    [Status.LENDERREVISIONREQUES]: {
        icon: <ClipboardEdit />,
        color: 'red',
        bgColor: 'bg-red-100',
        textColor: 'text-red-700',
        iconColor: 'text-red-500'
    },
    [Status.REVISIONREQUESTEDRES]: {
        icon: <FilePen />,
        color: 'yellow',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        iconColor: 'text-yellow-500'
    },
    [Status.LEVEL2QC]: {
        icon: <ShieldCheck />,
        color: 'blue',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-700',
        iconColor: 'text-blue-500'
    },
    [Status.WAITINGAPPROVAL]: {
        icon: <Loader />,
        color: 'yellow',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        iconColor: 'text-yellow-500'
    },
    [Status.DELAYED]: {
        icon: <Hourglass />,
        color: 'red',
        bgColor: 'bg-red-100',
        textColor: 'text-red-700',
        iconColor: 'text-red-500'
    },
    [Status.DRAFT]: {
        icon: <BookOpen />,
        color: 'gray',
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-700',
        iconColor: 'text-gray-500'
    },
    [Status.QUOTE]: {
        icon: <BookText />,
        color: 'blue',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-700',
        iconColor: 'text-blue-500'
    },
    [Status.PENDINGLVL2]: {
        icon: <Timer />,
        color: 'yellow',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        iconColor: 'text-yellow-500'
    },
    [Status.CANCELPENDING]: {
        icon: <AlertOctagon />,
        color: 'red',
        bgColor: 'bg-red-100',
        textColor: 'text-red-700',
        iconColor: 'text-red-500'
    },
    [Status.REVIEWESCALATION]: {
        icon: <ShieldAlert />,
        color: 'red',
        bgColor: 'bg-red-100',
        textColor: 'text-red-700',
        iconColor: 'text-red-500'
    },
    [Status.POSSIBLEREVISION]: {
        icon: <AlertCircle />,
        color: 'yellow',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        iconColor: 'text-yellow-500'
    },
    [Status.SPOTHIRE]: {
        icon: <Award />,
        color: 'blue',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-700',
        iconColor: 'text-blue-500'
    },
    [Status.APPRAISERDELAY]: {
        icon: <Clock />,
        color: 'red',
        bgColor: 'bg-red-100',
        textColor: 'text-red-700',
        iconColor: 'text-red-500'
    },
    [Status.LEVEL3QC]: {
        icon: <Scale />,
        color: 'blue',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-700',
        iconColor: 'text-blue-500'
    },
    [Status.DOCUMENTREVIEW]: {
        icon: <FileCheck />,
        color: 'blue',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-700',
        iconColor: 'text-blue-500'
    },
    [Status.PENDINGDELIVERY]: {
        icon: <Send />,
        color: 'yellow',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        iconColor: 'text-yellow-500'
    },
    [Status.DUPLICATEREVIEW]: {
        icon: <Eye />,
        color: 'blue',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-700',
        iconColor: 'text-blue-500'
    },
    [Status.CHASEESCALATION]: {
        icon: <FileWarning />,
        color: 'red',
        bgColor: 'bg-red-100',
        textColor: 'text-red-700',
        iconColor: 'text-red-500'
    },
    [Status.LEADERSHIPQC]: {
        icon: <ClipboardCheck />,
        color: 'blue',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-700',
        iconColor: 'text-blue-500'
    },
};

export const defaultStatusMetadata: StatusMetadata = {
    icon: <HelpCircle />,
    color: 'gray',
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-700',
    iconColor: 'text-gray-500'
};

// Utility function to get metadata for a specific status
export const getStatusMetadata = (statusId: number): StatusMetadata => {
    if (Object.values(Status).includes(statusId as Status)) {
        return statusMetadata[statusId as Status];
    }
    return defaultStatusMetadata;
};